import React, { useState } from 'react';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Switch,
    FormControlLabel,
    Slider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    ExpandMore,
    Save,
    RestartAlt,
    TrendingUp,
    Schedule,
    Notifications,
    Info,
    GridView
} from '@mui/icons-material';

const SettingsPage = () => {
    const { settings, updateSetting, resetSettings } = useSettings();
    const [saveStatus, setSaveStatus] = useState(null);

    const handleSettingChange = (category, field, value) => {
        updateSetting(category, field, value);
    };

    const handleArrayChange = (category, field, value) => {
        const keywords = value.split(',').map(k => k.trim()).filter(k => k.length > 0);
        handleSettingChange(category, field, keywords);
    };

    const handleSave = async () => {
        setSaveStatus('saving');
        try {
            // Save high-value criteria to backend
            const highValuePayload = {
                minPageViews: settings.highValueCriteria.minPageViews,
                highValueSuburbs: settings.highValueCriteria.highValueSuburbs,
                enableMultipleProducts: settings.highValueCriteria.enableMultipleProducts,
                priorityKeywords: settings.highValueCriteria.priorityKeywords
            };

            await axios.post('/api/leads/config/highvalue', highValuePayload);

            console.log('Settings saved successfully:', settings);
            setSaveStatus('success');
            setTimeout(() => setSaveStatus(null), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus(null), 3000);
        }
    };

    const handleReset = () => {
        resetSettings();
    };

    return (
        <Box sx={{
            width: '100%',
            height: '100vh',
            bgcolor: '#121212',
            color: '#e0e0e0',
            pt: 8, // Account for fixed top menu bar
            overflow: 'auto'
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                px: 3 // Add padding to header content
            }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                    Settings
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RestartAlt />}
                        onClick={handleReset}
                        sx={{
                            borderColor: '#666',
                            color: '#fff',
                            '&:hover': {
                                borderColor: '#ff9800',
                                bgcolor: 'rgba(255, 152, 0, 0.1)'
                            }
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                        disabled={saveStatus === 'saving'}
                        sx={{
                            bgcolor: '#4caf50',
                            '&:hover': {
                                bgcolor: '#45a049'
                            }
                        }}
                    >
                        {saveStatus === 'saving' ? 'Saving...' : 'Save Settings'}
                    </Button>
                </Box>
            </Box>

            {/* Save Status Alert */}
            {saveStatus && (
                <Box sx={{ px: 3 }}>
                    <Alert
                        severity={saveStatus === 'success' ? 'success' : saveStatus === 'error' ? 'error' : 'info'}
                        sx={{ mb: 3 }}
                    >
                        {saveStatus === 'success' && 'Settings saved successfully!'}
                        {saveStatus === 'error' && 'Failed to save settings. Please try again.'}
                        {saveStatus === 'saving' && 'Saving settings...'}
                    </Alert>
                </Box>
            )}

            <Box sx={{
                px: 3 // Add padding to content
            }}>
                <Grid container spacing={4}>
                    {/* High Value Lead Criteria */}
                    <Grid item xs={12}>
                        <Accordion
                            sx={{
                                bgcolor: '#2a2a2a',
                                border: '1px solid #333',
                                borderRadius: 2,
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore sx={{ color: '#fff' }} />}
                                sx={{
                                    py: 2,
                                    borderBottom: '1px solid #333'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <TrendingUp sx={{ color: '#4caf50' }} />
                                    <Typography variant="h6" sx={{
                                        color: '#fff',
                                        fontWeight: 600
                                    }}>
                                        High Value Lead Criteria
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ py: 4, px: 3 }}>
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Minimum Lead Value"
                                            type="number"
                                            value={settings.highValueCriteria.minLeadValue}
                                            onChange={(e) => handleSettingChange('highValueCriteria', 'minLeadValue', Number(e.target.value))}
                                            InputProps={{
                                                style: { color: '#fff' },
                                                startAdornment: <Typography sx={{ color: '#9e9e9e', mr: 1 }}>$</Typography>
                                            }}
                                            InputLabelProps={{ style: { color: '#9e9e9e' } }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: '#444' },
                                                    '&:hover fieldset': { borderColor: '#666' },
                                                    '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Response Time Threshold"
                                            type="number"
                                            value={settings.highValueCriteria.responseTimeThreshold}
                                            onChange={(e) => handleSettingChange('highValueCriteria', 'responseTimeThreshold', Number(e.target.value))}
                                            InputProps={{
                                                style: { color: '#fff' },
                                                endAdornment: <Typography sx={{ color: '#9e9e9e', ml: 1 }}>min</Typography>
                                            }}
                                            InputLabelProps={{ style: { color: '#9e9e9e' } }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: '#444' },
                                                    '&:hover fieldset': { borderColor: '#666' },
                                                    '&.Mui-focused fieldset': { borderColor: '#4caf50' }
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>

                    {/* Dashboard Settings */}
                    <Grid item xs={12}>
                        <Accordion
                            sx={{
                                bgcolor: '#2a2a2a',
                                border: '1px solid #333',
                                borderRadius: 2,
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore sx={{ color: '#fff' }} />}
                                sx={{
                                    py: 2,
                                    borderBottom: '1px solid #333'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Info sx={{ color: '#9c27b0' }} />
                                    <Typography variant="h6" sx={{
                                        color: '#fff',
                                        fontWeight: 600
                                    }}>
                                        Dashboard Settings
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ py: 4, px: 3 }}>
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <Typography gutterBottom sx={{ color: '#9e9e9e' }}>
                                            Auto Refresh Interval (seconds)
                                        </Typography>
                                        <Slider
                                            value={settings.dashboard.autoRefreshInterval}
                                            onChange={(_, value) => handleSettingChange('dashboard', 'autoRefreshInterval', value)}
                                            min={10}
                                            max={300}
                                            marks={[
                                                { value: 10, label: '10s' },
                                                { value: 60, label: '1m' },
                                                { value: 300, label: '5m' }
                                            ]}
                                            sx={{ color: '#9c27b0' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel sx={{ color: '#9e9e9e' }}>Default Date Range</InputLabel>
                                            <Select
                                                value={settings.dashboard.defaultDateRange}
                                                onChange={(e) => handleSettingChange('dashboard', 'defaultDateRange', e.target.value)}
                                                sx={{ color: '#fff' }}
                                            >
                                                <MenuItem value={7}>Last 7 days</MenuItem>
                                                <MenuItem value={30}>Last 30 days</MenuItem>
                                                <MenuItem value={90}>Last 90 days</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default SettingsPage;
